import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { get, send, type SyncStatus } from "./api.js";

/**
 * How fresh the mirror is, asked once for the whole app.
 *
 * The polling used to live inside the badge that draws it. It moved here
 * because a second thing now needs the answer: a page showing bookings has to
 * know when the mirror has moved on underneath it, and two components each
 * polling on their own timer would ask twice as often for the same fact.
 */

interface SyncState {
  status: SyncStatus | null;
  busy: boolean;
  refreshBusy: boolean;
  refreshError: string | null;
  syncNow: () => Promise<void>;
  refreshMyDiary: () => Promise<void>;
}

const IDLE_POLL = 60_000;
const BUSY_POLL = 3_000;
const INITIAL_POLL_DELAY = 1_000;
export const MIRROR_REFRESHED_EVENT = "wmf:mirror-refreshed";

const SyncContext = createContext<SyncState | null>(null);

export function SyncProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const poll = useCallback(async () => {
    try {
      const next = await get<SyncStatus>("/admin/sync-status");
      setStatus(next);
      // Check back quickly while a sync is in flight, so the line settles as
      // soon as it finishes rather than up to a minute later.
      timer.current = setTimeout(() => void poll(), next.running ? BUSY_POLL : IDLE_POLL);
    } catch {
      // A failed status check is not worth an error on screen; the next one
      // will either work or the whole page will have failed anyway.
      timer.current = setTimeout(() => void poll(), IDLE_POLL);
    }
  }, []);

  useEffect(() => {
    // Sync status is useful chrome, but not a prerequisite for Today/Diary.
    // Give the first page read a head start rather than competing for the same
    // small database pool during the initial burst of authenticated requests.
    timer.current = setTimeout(() => void poll(), INITIAL_POLL_DELAY);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [poll]);

  const syncNow = useCallback(async () => {
    setBusy(true);
    try {
      await send("POST", "/admin/sync-now");
    } catch {
      // Ignored deliberately: whether it started is answered by the next poll,
      // which is more reliable than this response.
    } finally {
      setBusy(false);
      if (timer.current) clearTimeout(timer.current);
      void poll();
    }
  }, [poll]);

  const refreshMyDiary = useCallback(async () => {
    if (refreshBusy) return;
    setRefreshBusy(true);
    setRefreshError(null);
    try {
      await send("POST", "/admin/refresh-my-diary", {});
      // Only reads that explicitly declare a mirror dependency listen for this
      // event. Unrelated configuration and queues stay still.
      window.dispatchEvent(new Event(MIRROR_REFRESHED_EVENT));
    } catch (cause: unknown) {
      setRefreshError(cause instanceof Error ? cause.message : "Could not refresh your diary.");
    } finally {
      setRefreshBusy(false);
    }
  }, [refreshBusy]);

  return (
    <SyncContext.Provider
      value={{ status, busy, refreshBusy, refreshError, syncNow, refreshMyDiary }}
    >
      {children}
    </SyncContext.Provider>
  );
}

export function useSync(): SyncState {
  const value = useContext(SyncContext);
  if (!value) throw new Error("useSync must be used inside SyncProvider");
  return value;
}

/**
 * When the mirror last finished changing. Null until it has ever finished.
 *
 * A failed run does not count: nothing was written, so nothing on screen is
 * out of date because of it.
 */
function mirrorMark(status: SyncStatus | null): string | null {
  if (!status?.last || status.last.error) return null;
  return status.last.finishedAt;
}

/**
 * Whether the mirror has been updated since this page loaded its data.
 *
 * Deliberately a question rather than an action. Reloading on its own would
 * take the page out from under whoever is reading it, and on a page holding
 * unsaved work — a half-written lesson note — it would take that too. So the
 * page asks, and a person decides.
 *
 * The diary holds no unsaved work and could refresh itself silently, but rows
 * moving under a cursor mid-click is its own small loss, and the prompt costs
 * one click. Pass the page's `loading` flag: whatever the mirror says when a
 * load finishes is what that data reflects.
 *
 * After a confirmed staff booking, move or cancellation, Diary temporarily
 * consumes one subsequent sync update automatically, waiting for any active
 * read to finish first. It then returns to this normal prompt policy.
 */
export function useNewSinceLoad(loading: boolean): boolean {
  const { status } = useSync();
  const mark = mirrorMark(status);

  const [seen, setSeen] = useState<string | null>(mark);
  // Read through a ref so the effect can take the newest mark without
  // re-running — and resetting itself — every time the poll returns.
  const latest = useRef(mark);
  latest.current = mark;

  useEffect(() => {
    if (!loading) setSeen(latest.current);
  }, [loading]);

  return mark !== null && seen !== mark;
}
